import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { environment } from '../../../environments/environment';

import { UtmPreservationService } from './utm-preservation.service';
import { UtmStorageService } from './utm-storage.service';

@Injectable({
  providedIn: 'root',
})
export class UrlService {
  private platformId = inject(PLATFORM_ID);
  private utmPreservationService = inject(UtmPreservationService);
  private utmStorageService = inject(UtmStorageService);


  getBaseUrl(): string {
    const basePath = environment.basePath;
    return `${window.location.origin}${basePath}`;
  }

  buildUrl(route: string, includeUtm: boolean = true, onlyFromCurrentUrl: boolean = false): string {
    const cleanRoute = route.startsWith('/') ? route.substring(1) : route;
    const url = `${this.getBaseUrl()}/${cleanRoute}`;
    
    // Add UTM parameters if they exist and includeUtm is true
    if (includeUtm && isPlatformBrowser(this.platformId)) {
      const utmParams = this.getUtmParamsAsObject(onlyFromCurrentUrl);
      
      if (Object.keys(utmParams).length > 0) {
        // Use URLSearchParams to properly construct the URL
        const urlObj = new URL(url);
        Object.entries(utmParams).forEach(([key, value]) => {
          urlObj.searchParams.set(key, value);
        });
        return urlObj.toString();
      }
    }
    
    return url;
  }

  getOrigin(): string {
    return window.location.origin;
  }

  private getUtmParamsAsObject(onlyFromCurrentUrl: boolean = false): Record<string, string> {
    if (!isPlatformBrowser(this.platformId)) {
      return {};
    }

    const params: Record<string, string> = {};
    
    // If onlyFromCurrentUrl is true, only check current URL (for email URLs)
    if (onlyFromCurrentUrl) {
      const urlParams = new URLSearchParams(window.location.search);
      const urlUtmSource = urlParams.get('utm_source');
      
      if (urlUtmSource) {
        // Use UTM params from URL only
        urlParams.forEach((value, key) => {
          if (key.toLowerCase().startsWith('utm_')) {
            params[key] = value;
          }
        });
      }
      return params;
    }
    
    // Otherwise, check preserved params first (they are more reliable)
    if (!this.utmPreservationService.isUtmEnabled()) {
      return {};
    }

    const preservedParams = this.utmPreservationService.preservedUtmParams;
    
    if (preservedParams && Object.keys(preservedParams).length > 0) {
      Object.entries(preservedParams).forEach(([key, value]) => {
        if (key.toLowerCase().startsWith('utm_')) {
          params[key] = value;
        }
      });
    }
    
    // If no preserved params in memory, check storage directly
    if (Object.keys(params).length === 0) {
      const trackingData = this.utmStorageService.getTrackingData();
      if (trackingData.utm_params && Object.keys(trackingData.utm_params).length > 0) {
        Object.entries(trackingData.utm_params).forEach(([key, value]) => {
          if (key.toLowerCase().startsWith('utm_')) {
            params[key] = value;
          }
        });
      }
    }
    
    // If still no params, check URL params as final fallback
    if (Object.keys(params).length === 0) {
      const urlParams = new URLSearchParams(window.location.search);
      const urlUtmSource = urlParams.get('utm_source');
      
      if (urlUtmSource) {
        // Use UTM params from URL
        urlParams.forEach((value, key) => {
          if (key.toLowerCase().startsWith('utm_')) {
            params[key] = value;
          }
        });
      }
    }
    
    return params;
  }
}
